import { useState, type ReactNode } from 'react'
import { Button } from './Button'
import { BackLink } from './BackLink'
import { ButtonLink } from './ButtonLink'
import { ButtonPreview } from './ButtonPreview'
import { SearchField } from './SearchField'
import { SegmentedControl } from './SegmentedControl'
import { CategoryChip } from './CategoryChip'
import { Checkbox } from './Checkbox'
import { Select } from './Select'
import { Switch } from './Switch'
import { InlineLink } from './InlineLink'
import { Meter } from './Meter'
import { DataGrid, type Column } from './DataGrid'
import { AlertDialog, ConfirmDialog } from './Dialog'
import { Field } from './Field'
import { GoogleG } from './GoogleG'
import { IconGrid } from './IconGrid'
import { META } from '../icons/categoryIconPaths'
import { InstitutionAvatar } from './InstitutionAvatar'
import { ListRow, SectionHeader } from './ListRow'
import { MerchantAvatar } from './MerchantAvatar'
import { Money } from './Money'
import { PersonAvatar } from './PersonAvatar'
import {
  AlertPanel,
  AssistantPanel,
  HeroTile,
  Panel,
  SunkWell,
} from './Panel'
import { TagEditor } from './TagEditor'
import { Toast } from './Toast'

/**
 * Every primitive, with a representative instance — Task 19.
 *
 * This is what makes "added a primitive without adding its states" a failing
 * build rather than a discovery in production. `StatesReference.test.tsx`
 * compares this list against the modules on disk.
 *
 * `.tsx`, not the plan's `.ts`: the entries contain JSX. Beside the primitives
 * rather than at the platform root, because it must stay in step with them and
 * `StatesReference.test.tsx` compares the two.
 *
 * Not derived, unlike `surfaceContract.test.tsx`'s surface list — a surface IS
 * its marker class, so it can be inferred, but a REPRESENTATIVE INSTANCE cannot:
 * `<Money cents={-11844} kind="flow" />` encodes that -11844 is an interesting
 * value and that `flow` colours by sign. The derivation happens where derivation
 * is possible, in the coverage check against the module glob.
 */

/**
 * An entry is a primitive, OR a named scenario of one.
 *
 * `of` is what stops `name` quietly ceasing to mean "a component". #126 needed a
 * second `DataGrid` instance to reach the grid's own empty, so the list no
 * longer maps one-to-one onto components — and without this field nothing
 * records that, the coverage check can only run one way, and a typo'd entry name
 * produces a section documenting nothing rather than a failure.
 *
 * With it the check runs BOTH ways: every exported component has an entry, and
 * every entry names a component or declares which one it is a scenario of.
 */
export type PrimitiveEntry = { name: string; render: () => ReactNode; of?: string }

/** The grid's fixture, hoisted so the reconciliation accessor can see its type. */
type GridRow = { merchant: string; cents: number }

/**
 * The second grid entry's name — #126.
 *
 * Exported because `StatesReference.test.tsx` addresses that section by heading
 * and a literal in both places drifts. Precedent is `SURFACE_CLASSES` and
 * `AS_OF`, exported for the same reason: a duplicated literal is the worse leak.
 */
export const EMPTY_GRID = 'DataGrid — no matching rows'

/**
 * Shared by both grid entries, so the two differ only in the thing under test.
 *
 * **Annotated, not inferred.** As a bare `const` the literals lose their
 * contextual type, so assignment to `Column<GridRow>[]` becomes a plain
 * structural check and excess-property checking is gone — `aling: 'end'`
 * compiles clean and silently renders the amount column start-aligned, past
 * `datagrid__cell--end`'s `tabular-nums`. Confirmed against `tsc --strict`:
 * TS2561 with this annotation, no diagnostic without it. The `align: 'end' as
 * const` this replaces was the symptom, not the fix.
 */
const GRID_COLUMNS: Column<GridRow>[] = [
  { key: 'm', header: 'Merchant', width: 'minmax(0, 2fr)', render: (r) => r.merchant },
  {
    key: 'a',
    header: 'Amount',
    width: 'minmax(0, 1fr)',
    align: 'end',
    render: (r) => <Money cents={r.cents} kind="flow" />,
  },
]

/**
 * The two dialog samples.
 *
 * **Not exported, and that is load-bearing.** `StatesReference.test.tsx` reads
 * every capitalised export of every module in this directory and demands a
 * registry entry for it, so an exported `ConfirmDialogSample` would be a
 * primitive owing five states of its own.
 *
 * `onConfirm` rejects rather than resolves: the failure branch is the state
 * worth documenting — the one that stays open, names what went wrong and hands
 * the controls back — and a resolving sample would show a dialog that vanishes.
 */
function AlertDialogSample() {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button variant="outline" size="page" onClick={() => setOpen(true)}>
        Show the alert
      </Button>
      <AlertDialog
        open={open}
        onClose={() => setOpen(false)}
        title="Royal Bank hasn't synced since Tuesday"
        body="Reconnect the account to bring its transactions up to date."
        acknowledge="Got it"
      />
    </>
  )
}

/**
 * `IconGrid` needs a live `value`/`onChange` pair to show selection at
 * all — a static `value` prop would leave every radio permanently unchecked
 * (nothing ever calls `onChange`) or permanently checked (nothing ever moves
 * it), neither of which is the control's real behaviour.
 *
 * The set is not derived at runtime — `META` is a source import, so the full
 * 88-glyph taxonomy is what a real caller (`CategoryForm.tsx`) passes, and
 * that is what this page documents rather than a hand-picked subset.
 */
function IconGridSample() {
  const [value, setValue] = useState<string | null>('groceries')
  return (
    <IconGrid
      icons={Object.values(META).map((m) => m.slug)}
      value={value}
      onChange={setValue}
      label="Category icon"
    />
  )
}

function ConfirmDialogSample() {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button variant="outline" size="page" onClick={() => setOpen(true)}>
        Show the confirmation
      </Button>
      <ConfirmDialog
        open={open}
        mode="states_reference"
        onCancel={() => setOpen(false)}
        onConfirm={() => Promise.reject(new Error('sample'))}
        errorFor={() => "Royal Bank didn't answer. Nothing has changed — try again in a moment."}
        title="Disconnect Royal Bank?"
        body="Its 214 transactions stay, and no new ones arrive until you reconnect it."
        confirmLabel="Disconnect"
        pendingLabel="Disconnecting…"
        destructive
      />
    </>
  )
}

export const PRIMITIVES: PrimitiveEntry[] = [
  { name: 'Button', render: () => <Button variant="primary" size="page">Add transaction</Button> },
  {
    // A `Button`'s look with nothing to press — what a locked `PremiumGate`
    // shows in place of the live control. A `<span>`, so the specimen is the one
    // button-skinned thing on this page that a stray click or Tab cannot reach.
    name: 'ButtonPreview',
    render: () => (
      <ButtonPreview variant="primary" size="page">
        Add transaction
      </ButtonPreview>
    ),
  },
  {
    // The anchor half of `Button` — see ButtonLink.tsx for why it is a separate
    // component rather than an `as` prop. Rendered at `size="form"`, its own
    // default and the size every error screen uses, because that is the pairing
    // #334 caught at 42.8px against a 44px floor.
    //
    // `to`, not `href`: a specimen that navigated off the reference screen on a
    // stray click would be a trap in a page whose whole job is being clicked.
    name: 'ButtonLink',
    render: () => (
      <ButtonLink variant="primary" to="/states">
        Back to Overview
      </ButtonLink>
    ),
  },
  {
    // Two of them, adjacent and separated by a "·" — the SignIn arrangement,
    // because a single specimen cannot show the property #169 is actually
    // about. The target has to be 44px tall below 640px AND the gap to its
    // neighbour has to survive; a lone link demonstrates only the first.
    name: 'InlineLink',
    render: () => (
      <>
        <InlineLink to="/states">Forgot your password?</InlineLink>
        {' · '}
        <InlineLink to="/states">Create an account</InlineLink>
      </>
    ),
  },
  {
    /**
     * The mark, on its own, because it is the one thing on these screens that
     * must look identical in both themes — Google forbids recolouring it, and
     * the states reference is where a reader can see the two themes side by
     * side and notice if it ever stops being true.
     *
     * Rendered beside its button so the 18px/10px relationship the guidelines
     * specify is visible rather than only asserted.
     */
    name: 'GoogleG',
    render: () => (
      <>
        <GoogleG />
        <Button variant="outline" size="form" className="btn--google">
          <GoogleG />
          Continue with Google
        </Button>
      </>
    ),
  },
  {
    // One row per skin: the States reference is where every skin and both orientations are seen.
    name: 'Meter',
    render: () => (
      <>
        <Meter label="Neutral" value={40} valueText="40% used" />
        <Meter label="Spent" skin="spent" value={55} valueText="55% used" />
        <Meter label="Warn" skin="warn" value={88} valueText="88% used, close to limit" />
        <Meter label="Over" skin="over" value={120} valueText="120% used, over budget" />
        <HeroTile>
          <Meter label="On accent" skin="onAccent" value={60} valueText="60% of assets" />
        </HeroTile>
        <div style={{ display: 'flex', blockSize: 96, inlineSize: 40 }}>
          <Meter label="Column" skin="spent" orientation="column" value={65} valueText="65% used" />
        </div>
      </>
    ),
  },
  { name: 'BackLink', render: () => <BackLink to="/states">All items</BackLink> },
  {
    name: 'SearchField',
    render: () => (
      <SearchField label="Search merchants" placeholder="Search merchants" value="" onChange={() => {}} />
    ),
  },
  {
    name: 'SegmentedControl',
    render: () => (
      <SegmentedControl
        label="Type"
        name="registry-segmented"
        options={[
          { value: 'all', label: 'All types' },
          { value: 'business', label: 'Business' },
          { value: 'person', label: 'Person' },
        ]}
        value="all"
        onChange={() => {}}
      />
    ),
  },
  { name: 'Field', render: () => <Field label="Email" defaultValue="sam@example.test" /> },
  {
    // The invalid state is the one M1-R2 exists for, and it is a different
    // rendering rather than a different colour — the message and the
    // `aria-invalid` are what a colour-blind user has.
    name: 'Field — invalid',
    of: 'Field',
    render: () => (
      <Field
        label="Email"
        defaultValue="sam@"
        error="That doesn't look like an email address."
      />
    ),
  },
  {
    // Both directions of `kind="flow"` — #126. Every figure on this page was
    // negative or a `stock`, so `--pos` and the leading `+` never rendered and
    // the money-in half was undocumented by the surface whose job is
    // documenting. `Money.test.tsx` covers the token; what was missing is the
    // PRESENTATION, and a screenshot diff cannot catch a regression in a colour
    // the page never paints.
    //
    // 312750 deliberately, and NOT 428000 — $4,280 is the figure M0-R24 uses as
    // its canonical STOCK example ("$4,280 sitting in chequing is not money
    // coming in"). On the one surface whose job is teaching flow from stock,
    // the PRD's stock exemplar as the positive flow invites exactly the wrong
    // reading. An odd figure reads as a deposit rather than a balance.
    //
    // The two figures are separated by the `gap` of whichever ancestor lays
    // them out — `.states__cell` in `success`, `Async`'s own wrapper in `stale`
    // — because a fragment adds no box of its own. Both are `flex column
    // gap: 7px` today. If either became `row` or block flow the figures would
    // run together and jsdom, which does no layout, would never see it.
    name: 'Money',
    render: () => (
      <>
        <Money cents={-11844} kind="flow" />
        <Money cents={312750} kind="flow" />
      </>
    ),
  },
  {
    // Rendered with `onActivate` so the affordance the button gets — hover,
    // focus-visible, a name built from the label — is what shows on the page,
    // rather than the inert div shape `ListRow.test.tsx` already covers.
    name: 'ListRow',
    render: () => <ListRow label="Groceries" sublabel="7 subcategories" onActivate={() => {}} />,
  },
  {
    // The disabled skin `controlTokens.test.ts`'s specificity floor protects —
    // #43. A control that CAN be disabled has to be shown disabled somewhere,
    // or this page's own premise ("everything documented is visible") is false
    // for the one state that guard exists to keep correct.
    name: 'ListRow — disabled',
    of: 'ListRow',
    render: () => (
      <ListRow label="Groceries" sublabel="7 subcategories" onActivate={() => {}} disabled />
    ),
  },
  {
    name: 'SectionHeader',
    render: () => <SectionHeader title="Expense" count={12} />,
  },
  {
    // No colour — #454 removed it. `CategoryChip` used to take a `color`
    // prop and this section documented the null-colour case as a distinct
    // scenario; there is only one look now, for every category.
    name: 'CategoryChip',
    render: () => <CategoryChip name="Groceries" />,
  },
  {
    // The logo path — Task 2 of the merchants module (#41's epic). A real
    // asset under `public/`, not a URL that has never resolved — fix round 1,
    // finding 5: `https://cdn.example.test/...` never loads in a real
    // browser, so `onError` fired on every page view and this design
    // system's own shop window documented the logo treatment as a grey disc
    // with an "S". jsdom's tests stayed green regardless (no fetch there),
    // which is exactly why the browser sweep is what caught it.
    name: 'MerchantAvatar',
    render: () => (
      <MerchantAvatar
        merchant={{
          id: 'm1',
          name: 'Save-On-Foods',
          type: 'business',
          color: null,
          logo_url: '/states-reference/sample-merchant-logo.png',
          is_global: false,
        }}
      />
    ),
  },
  {
    // No logo — the swatch-plus-initial fallback, with a real server colour.
    // `var(--s3)`, not a literal hex: this file is source, and a real hex
    // here trips the colour guard the component exists to stay clear of.
    name: 'MerchantAvatar — no logo',
    of: 'MerchantAvatar',
    render: () => (
      <MerchantAvatar
        merchant={{
          id: 'm2',
          name: 'Shell',
          type: 'business',
          color: 'var(--s3)',
          logo_url: null,
          is_global: false,
        }}
      />
    ),
  },
  {
    // The shared/global badge, and `size="detail"` — Task 2's other size,
    // first exercised here the way `CategoryChip`'s `size="chip"` was first
    // exercised in `CategoryDetail.tsx`.
    name: 'MerchantAvatar — shared',
    of: 'MerchantAvatar',
    render: () => (
      <MerchantAvatar
        size="detail"
        merchant={{
          id: 'm3',
          name: 'Amazon',
          type: 'business',
          color: 'var(--s2)',
          logo_url: null,
          is_global: true,
        }}
      />
    ),
  },
  {
    // A member's identity — initials on --chip, never a photo or a hue. See
    // `PersonAvatar.tsx` for why: `MembershipResponse` carries no image URL,
    // and D14's hue wheel is unsettled across issues 290 and 299. This is what
    // feeds the contrast sweep's measurement of --ink on --chip.
    name: 'PersonAvatar',
    render: () => <PersonAvatar name="Sushil Mehta" email="sushil@example.com" />,
  },
  {
    // The no-name fallback — the email prefix, as iOS derives it. `display_name`
    // is nullable on `MembershipResponse`; this is what that row looks like.
    name: 'PersonAvatar — no name',
    of: 'PersonAvatar',
    render: () => <PersonAvatar name={null} email="anita@example.com" />,
  },
  {
    // An institution's identity — `InstitutionsPanel.tsx`'s own monogram
    // column (M5 Ticket 2, #425 §A3). No logo — two initials on --chip, the
    // same neutral fallback `PersonAvatar` uses and for the identical reason
    // (no per-institution hue; see the component's own docblock).
    name: 'InstitutionAvatar',
    render: () => <InstitutionAvatar name="Royal Bank of Canada" logoUrl={null} />,
  },
  {
    // The logo path — a real asset under `public/`, matching `MerchantAvatar
    // `'s own fix round 1 finding 5: a URL that never resolves leaves this
    // page's own shop window showing a broken image, invisible to a jsdom
    // test and only caught by the browser sweep.
    name: 'InstitutionAvatar — logo',
    of: 'InstitutionAvatar',
    render: () => (
      <InstitutionAvatar
        name="Save-On-Foods"
        logoUrl="/states-reference/sample-merchant-logo.png"
      />
    ),
  },
  {
    name: 'IconGrid',
    render: () => <IconGridSample />,
  },
  {
    // The disabled skin `controlTokens.test.ts`'s specificity floor
    // protects: a disabled skin nobody renders is a skin nobody sees. Static
    // rather than the stateful sample: a disabled radio never calls
    // `onChange`, so there is nothing for a `useState` pair to do here.
    name: 'IconGrid — disabled',
    of: 'IconGrid',
    render: () => (
      <IconGrid
        icons={['groceries', 'coffee-shops', 'transportation']}
        value="groceries"
        onChange={() => {}}
        label="Category icon"
        disabled
      />
    ),
  },
  {
    // All four states side by side: the tick and the dash are what carry the
    // state, so the reference has to show them rather than one box.
    name: 'Checkbox',
    render: () => (
      <>
        <Checkbox checked={false} onChange={() => {}} label="Unchecked" />
        <Checkbox checked onChange={() => {}} label="Checked" />
        <Checkbox checked="mixed" onChange={() => {}} label="Mixed" />
        <Checkbox checked onChange={() => {}} label="Disabled" disabled />
      </>
    ),
  },
  {
    // Off, on and disabled: the thumb's side is what carries the state, so the reference shows both.
    name: 'Switch',
    render: () => (
      <>
        <Switch checked={false} onChange={() => {}} label="Off" />
        <Switch checked onChange={() => {}} label="On" />
        <Switch checked onChange={() => {}} label="Disabled" disabled />
      </>
    ),
  },
  {
    // Closed, and disabled: the drawn chevron and the dimmed boundary are what
    // the reference has to show, since a native arrow carries no declaration.
    name: 'Select',
    render: () => (
      <>
        <Select label="Account" defaultValue="all">
          <option value="all">All accounts</option>
        </Select>
        <Select label="Account (disabled)" defaultValue="all" disabled>
          <option value="all">All accounts</option>
        </Select>
      </>
    ),
  },
  {
    // One tag with its remove button, and the add affordance; then both disabled.
    // In a Panel: the chip's tint is composed for an opaque --surface, which is
    // where every real instance sits (the transaction panel, bill settings).
    name: 'TagEditor',
    render: () => (
      <Panel>
        <TagEditor tags={['groceries']} suggestions={[]} disabled={false} reasonId={undefined} onChange={() => {}} />
        <TagEditor tags={['groceries']} suggestions={[]} disabled reasonId={undefined} onChange={() => {}} />
      </Panel>
    ),
  },
  { name: 'Panel', render: () => <Panel><span>Standard panel</span></Panel> },
  { name: 'HeroTile', render: () => <HeroTile><Money cents={18492040} variant="hero" /></HeroTile> },
  { name: 'SunkWell', render: () => <SunkWell><span>Sunk well</span></SunkWell> },
  {
    name: 'AssistantPanel',
    render: () => <AssistantPanel><span>Assistant</span></AssistantPanel>,
  },
  { name: 'AlertPanel', render: () => <AlertPanel><span>Overdue · Jul 22</span></AlertPanel> },
  {
    /**
     * The `error` kind, deliberately — it is the one with a contrast obligation
     * worth looking at (--neg ink and boundary on an opaque --surface) and the
     * one whose copy this page should be teaching from. "Something went wrong"
     * is a defect where "Royal Bank hasn't synced since Tuesday" is possible.
     *
     * Rendered bare, outside the queue's live regions, with `paused` held and
     * `onDismiss` a no-op: this page documents the BOX, and a sample that
     * deleted itself five seconds after the page loaded would document nothing.
     *
     * The clock, the pause, the dismissal and the one-region-per-politeness
     * rule all belong to `ToastProvider` — which has no skin and no five states,
     * so no entry here — and are asserted in `Toast.test.tsx`, where they can
     * actually fail.
     */
    name: 'Toast',
    render: () => (
      <Toast
        id="states-reference"
        kind="error"
        message="Royal Bank hasn't synced since Tuesday."
        paused
        onDismiss={() => {}}
      />
    ),
  },
  {
    /**
     * A TRIGGER, not an open dialog — and the choice is forced.
     *
     * `Cell` calls `render()` for the `stale` and `success` states, so an entry
     * that returned an open `Overlay` would put two portalled modals on this
     * page per dialog, four in total: each traps focus, each sets `inert` on
     * every other child of `<body>`, and the page they document becomes
     * unreachable the moment it loads. Rendering one CLOSED is the other
     * failure — five empty cells under a heading, which is the "section
     * documenting nothing" this registry exists to prevent.
     *
     * A control that opens the real thing documents both: the affordance is
     * visible on the page, and the dialog is one click away in the state a
     * visual-regression pass wants to capture. The behaviour lives in
     * `Dialog.test.tsx`, where it can actually fail.
     */
    name: 'AlertDialog',
    render: () => <AlertDialogSample />,
  },
  {
    name: 'ConfirmDialog',
    render: () => <ConfirmDialogSample />,
  },
  {
    name: 'DataGrid',
    render: () => (
      // 18492040 is deliberate: at hero size it measures 231px of unbreakable
      // digits, which is what `statesReference.css`'s 260px track floor is sized
      // for and what `statesReference.test.tsx` pins. Do not "simplify" it.
      <DataGrid<GridRow>
        label="Sample transactions"
        // Required since #31: a generic grid cannot name the missing thing, so
        // the caller must. The reference page is a caller like any other.
        //
        // Provably dead — this entry always has rows, so `shown.length === 0` is
        // never true and this never mounts. Kept because the prop is required.
        //
        // Deliberately NOT given an action to match the live empty below. This
        // page's premise is that everything visible is documented and everything
        // documented is visible; an example control that can never render cuts
        // against it, and web-tester confirmed it renders zero buttons. The
        // pattern to copy is the reachable one in the entry below, which this
        // now points at instead of imitating.
        empty={<p>No sample rows — see the entry below for a grid empty that renders.</p>}
        columns={GRID_COLUMNS}
        groups={[
          {
            label: 'Tuesday, 28 July',
            subtotalCents: -18254,
            rows: [
              { merchant: 'Save-On-Foods', cents: -11844 },
              { merchant: 'Shell 4821', cents: -6410 },
            ],
          },
        ]}
        rowKey={(r) => r.merchant}
        // Opted in deliberately: this fixture IS the complete set its subtotal
        // covers, so the reference page proves the reconciliation path runs
        // rather than merely existing.
        amountCents={(r) => r.cents}
      />
    ),
  },
  {
    /**
     * The grid's OWN empty — #126.
     *
     * The entry above passes `empty` and never shows it: `Async` intercepts the
     * `empty` status and renders its own, so the page displayed both components
     * while making one of the two empties look like it did not exist. They are a
     * real distinction and this page exists to demonstrate it —
     *
     *   `Async`'s     no data at all.
     *   `DataGrid`'s  the filter matched nothing, and the column headers STAY
     *                 so you can see what would be there.
     *
     * Reached by giving the grid no groups under a `success` state, so `Async`
     * hands off and the grid decides for itself. A sixth column on the page
     * would not do it: the five are `Async`'s states, and a primitive's own
     * internal states are not `Async`'s to enumerate — which is the limit of
     * this page's shape. `of` is what states that on the page rather than only
     * here, and what lets the coverage check run both ways.
     */
    name: EMPTY_GRID,
    of: 'DataGrid',
    render: () => (
      <DataGrid<GridRow>
        label="Sample transactions, filtered"
        // Names the missing thing and offers one action, per CLAUDE.md — the
        // same bar the `Async` empty beside it is held to.
        // `size="panel"`, matching the action in `Async`'s empty two cells
        // away. This shipped as `size="inline"` — 24px, which `hitTarget.ts`
        // documents as "below the 44px floor even on desktop, deliberately —
        // it is for dense desktop rows". `.datagrid__empty` is a centred block
        // with 28px of padding holding an empty state's sole action, which is
        // the opposite of a dense row, and it was the only `size="inline"` call
        // site in the app. This page is what M6 and M7 will copy their empty
        // states from, so the first use of the smallest target should not be
        // established here.
        empty={
          <>
            <p>No rows match this filter.</p>
            <Button variant="outline" size="panel">Clear filter</Button>
          </>
        }
        columns={GRID_COLUMNS}
        groups={[]}
        rowKey={(r) => r.merchant}
      />
    ),
  },
]
